import {forwardRef, Ref, useMemo} from 'react';
import {
  Select as MUISelect,
  SelectProps as MUISelectProps,
  MenuItem,
  Slide,
  FormControl,
  Avatar,
  Typography,
  SxProps,
  ListSubheader,
  Stack,
  Box,
  SvgIcon,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import {Pallete} from '../../../../theme/bluebird';
import {DailyCareInput} from '../daily-care-input';

export interface Option {
  key: string;
  label: string;
  picture?: string;
  category?: string;
  border?: string;
}

export type DailyCareSelectProps = {
  options: Option[];
  label?: string;
  errors?: string;
  user?: boolean;
  categories?: {key: string; label: string}[];
} & MUISelectProps;

const avatarSx: SxProps = {
  width: '28px',
  height: '28px',
  marginRight: '8px',
};

export const DailyCareSelect = forwardRef(
  (
    {value, label, user, options, errors, categories, ...rest}: DailyCareSelectProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const hasError = !!errors;

    const list = useMemo(() => {
      if (!categories) return options;

      return categories.reduce((acc, category) => {
        const categoryOptions = options.filter((option) => option.category === category.key);
        acc.push(
          {key: category.key, label: category.label, category: 'category'},
          ...categoryOptions
        );
        return acc;
      }, [] as Option[]);
    }, [options, categories]);

    return (
      <FormControl sx={{width: '100%'}}>
        <MUISelect
          ref={ref}
          input={<DailyCareInput value={value || ''} label={label || rest.placeholder} />}
          MenuProps={{
            PaperProps: {
              style: {
                color: Pallete.gray.base,
              },
            },
          }}
          renderValue={() => {
            if (value) {
              const selected = options.find((option) => option.key === value);
              const category = categories?.find((cat) => cat.key === selected?.category);

              if (selected?.category) {
                return `${category?.label} > ${selected?.label}`;
              } else if (user && selected) {
                return (
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    marginLeft={rest.startAdornment ? '6px' : undefined}
                  >
                    <Avatar
                      sx={{
                        ...avatarSx,
                        border: selected?.border ? `1.5px solid ${selected.border}` : undefined,
                      }}
                      src={selected?.picture}
                    />
                    {selected?.label}
                  </Stack>
                );
              }

              return selected?.label;
            } else {
              return undefined;
            }
          }}
          {...rest}
          sx={{
            ...rest.sx,
            marginTop: value ? '2px' : undefined,
          }}
          placeholder={undefined}
        >
          {list.map(({key, label, picture, border, category}) => {
            if (category === 'category') {
              return (
                <ListSubheader
                  key={`select-option-${key}`}
                  sx={{
                    textTransform: 'capitalize',
                    fontSize: '14px',
                    fontWeight: 700,
                    lineHeight: '10px',
                    padding: '10px',
                    color: 'var(--dcm-gray)',
                  }}
                >
                  {label}
                </ListSubheader>
              );
            }
            return (
              <MenuItem
                key={`select-option-${key}`}
                value={key}
                sx={{padding: '10px', paddingLeft: category ? '20px' : '10px'}}
              >
                {user && (
                  <Avatar
                    src={picture}
                    sx={{...avatarSx, border: border ? `1.5px solid ${border}` : undefined}}
                  />
                )}
                <Typography sx={{fontSize: '14px', fontWeight: '400', lineHeight: '10px'}}>
                  {label}
                </Typography>
                <Box sx={{height: '22px', width: '28px', display: 'flex', marginLeft: 'auto'}}>
                  {value === key && (
                    <SvgIcon sx={{fontSize: 'small', margin: 'auto', mr: '9px'}}>
                      <CheckIcon />
                    </SvgIcon>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </MUISelect>
        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: Pallete.red.base}}>
            {errors}
          </span>
        </Slide>
      </FormControl>
    );
  }
);
